import { createUserWithEmailAndPassword, getAuth, sendEmailVerification, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, getFirestore, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { app } from '../firebase';
import { buildUserProfileDocument } from '../utils/profileDefaults';
import './AuthModal.css';

const auth = getAuth(app);
const db = getFirestore(app);

const AuthModal = ({ isOpen, onClose, initialMode = 'login', onAuthSuccess }) => {
    const navigate = useNavigate();
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState('');

    // Reset form when modal opens/closes or mode changes
    useEffect(() => {
        if (isOpen) {
            setMode(initialMode);
            setError('');
            setSuccess('');
            setPasswordStrength('');
        }
    }, [isOpen, initialMode]);

    // Reset form when mode changes
    useEffect(() => {
        setError('');
        setSuccess('');
        setPassword('');
        setConfirmPassword('');
        if (mode === 'login') {
            setName('');
            setUsername('');
        }
    }, [mode]);

    // Calculate password strength
    useEffect(() => {
        if (mode === 'signup' && password) {
            let strength = 0;
            if (password.length >= 6) strength++;
            if (password.length >= 8) strength++;
            if (/[A-Z]/.test(password)) strength++;
            if (/[a-z]/.test(password)) strength++;
            if (/[0-9]/.test(password)) strength++;
            if (/[^A-Za-z0-9]/.test(password)) strength++;

            if (strength <= 2) setPasswordStrength('weak');
            else if (strength <= 4) setPasswordStrength('medium');
            else setPasswordStrength('strong');
        } else {
            setPasswordStrength('');
        }
    }, [password, mode]);

    if (!isOpen) return null;

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // Email validation
            if (!validateEmail(email)) {
                setError('Please enter a valid email address.');
                setLoading(false);
                return;
            }

            if (mode === 'signup') {
                // Signup validation
                const cleanUsername = username
                    .toLowerCase()
                    .trim()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                const displayName = name.trim() || cleanUsername;

                if (!cleanUsername) {
                    setError('Public username is required.');
                    setLoading(false);
                    return;
                }
                if (cleanUsername.length < 3) {
                    setError('Username must be at least 3 characters.');
                    setLoading(false);
                    return;
                }
                if (password.length < 6) {
                    setError('Password must be at least 6 characters.');
                    setLoading(false);
                    return;
                }
                if (password !== confirmPassword) {
                    setError('Passwords do not match.');
                    setLoading(false);
                    return;
                }

                // Create user
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                
                // Update profile
                await updateProfile(userCredential.user, { displayName });
                
                // Send verification email (don't block on error)
                try {
                    await sendEmailVerification(userCredential.user);
                } catch (verifyErr) {
                    console.warn('Could not send verification email:', verifyErr);
                }
                
                // Create Firestore profile
                try {
                    await setDoc(
                        doc(db, 'profiles', userCredential.user.uid),
                        buildUserProfileDocument({
                            user: userCredential.user,
                            name: displayName,
                            username: cleanUsername,
                        }),
                        { merge: true }
                    );
                } catch (profileErr) {
                    console.error('Error creating profile:', profileErr);
                    // Don't fail signup if profile creation fails
                }

                setSuccess('Account created successfully! Redirecting...');
                
                // Close modal and redirect after short delay
                setTimeout(() => {
                    resetForm();
                    onClose();
                    if (onAuthSuccess) onAuthSuccess();
                    // Redirect to profile for new users
                    navigate('/profile');
                }, 1500);
            } else {
                // Login
                await signInWithEmailAndPassword(auth, email, password);
                setSuccess('Welcome back! Redirecting...');
                
                // Close modal after short delay
                setTimeout(() => {
                    resetForm();
                    onClose();
                    if (onAuthSuccess) onAuthSuccess();
                }, 1000);
            }
        } catch (err) {
            let errorMessage = 'An error occurred. Please try again.';
            
            // More specific error handling
            switch (err.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'This email is already registered. Please log in instead.';
                    // Auto-switch to login mode
                    setTimeout(() => {
                        setMode('login');
                        setError('');
                    }, 3000);
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address. Please check and try again.';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak. Please use a stronger password.';
                    break;
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email. Please sign up first.';
                    break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password. Please try again.';
                    break;
                case 'auth/invalid-credential':
                    errorMessage = 'Invalid email or password. Please check your credentials.';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many failed attempts. Please try again later or reset your password.';
                    break;
                case 'auth/user-disabled':
                    errorMessage = 'This account has been disabled. Please contact support.';
                    break;
                case 'auth/network-request-failed':
                    errorMessage = 'Network error. Please check your connection and try again.';
                    break;
                default:
                    if (err.message) {
                        errorMessage = err.message;
                    }
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setUsername('');
        setError('');
        setSuccess('');
        setPasswordStrength('');
        setShowPassword(false);
    };

    const switchMode = () => {
        const newMode = mode === 'login' ? 'signup' : 'login';
        setMode(newMode);
        resetForm();
    };

    const handleClose = () => {
        if (!loading) {
            resetForm();
            onClose();
        }
    };

    return (
        <div className="auth-modal-overlay" onClick={handleClose}>
            <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
                <button 
                    className="auth-modal-close" 
                    onClick={handleClose} 
                    aria-label="Close"
                    disabled={loading}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>

                <div className="auth-modal-header">
                    <h2>{mode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
                    <p>{mode === 'login' ? 'Sign in to continue' : 'Pick your public name and start selling.'}</p>
                </div>

                <form className="auth-form" onSubmit={handleSubmit}>
                    {error && (
                        <div className="auth-error-message">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="12"/>
                                <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="auth-success-message">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            <span>{success}</span>
                        </div>
                    )}

                    {mode === 'signup' && (
                        <div className="signup-name-grid">
                            <div className="form-group">
                                <label htmlFor="username">Public username *</label>
                                <input
                                    id="username"
                                    type="text"
                                    placeholder="metro-beats"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    disabled={loading}
                                    minLength={3}
                                    autoComplete="username"
                                />
                                <span className="form-helper">Shown on your profile.</span>
                            </div>
                            <div className="form-group">
                                <label htmlFor="name">Display name</label>
                                <input
                                    id="name"
                                    type="text"
                                    placeholder="Metro Beats"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    disabled={loading}
                                    autoComplete="name"
                                />
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                            id="email"
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value.trim())}
                            required
                            disabled={loading}
                            autoComplete="email"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <div className="password-input-wrapper">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder={mode === 'signup' ? 'Create a password (min. 6 characters)' : 'Enter your password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                                minLength={mode === 'signup' ? 6 : undefined}
                                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                            >
                                {showPassword ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                        <line x1="1" y1="1" x2="23" y2="23"/>
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                        {mode === 'signup' && password && (
                            <div className={`password-strength ${passwordStrength}`}>
                                <span>Password strength: </span>
                                <span className="strength-text">
                                    {passwordStrength === 'weak' && 'Weak'}
                                    {passwordStrength === 'medium' && 'Medium'}
                                    {passwordStrength === 'strong' && 'Strong'}
                                </span>
                            </div>
                        )}
                    </div>

                    {mode === 'signup' && (
                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm Password *</label>
                            <div className="password-input-wrapper">
                                <input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Confirm your password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    autoComplete="new-password"
                                />
                                {confirmPassword && password !== confirmPassword && (
                                    <span className="password-match-error">Passwords don't match</span>
                                )}
                            </div>
                        </div>
                    )}

                    <button type="submit" className="auth-submit-button" disabled={loading}>
                        {loading ? (
                            <>
                                <div className="button-spinner"></div>
                                <span>{mode === 'login' ? 'Signing in...' : 'Creating account...'}</span>
                            </>
                        ) : (
                            <span>{mode === 'login' ? 'Sign In' : 'Create Account'}</span>
                        )}
                    </button>
                </form>

                <div className="auth-modal-footer">
                    <p>
                        {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
                        <button 
                            type="button" 
                            className="switch-mode-button" 
                            onClick={switchMode} 
                            disabled={loading}
                        >
                            {mode === 'login' ? 'Sign Up' : 'Sign In'}
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
